import axios from 'axios';
import LOCAL_STORAGE_KEY from '../constants/localStorageKey';
import { getStorage } from '../utils/general.util';

/**
 * Renovación silenciosa de la sesión web con el refresh token que ya guardamos
 * en cada login (Verify.tsx / usePostAuthRedirect.ts) y que hasta ahora nunca
 * usábamos: con un 401 la web borraba el token y mandaba a /login.
 *
 * Es el mismo contrato que ya usa el shell nativo (heru-mobile-app,
 * `session.util.ts` + `executeWithRefresh` en `fetch.ts`):
 *
 *   POST {UME}/auth/refresh  { refresh_token }
 *   → { id, access_token: { access_token }, refresh_token: { refresh_token } }
 *   → 401 si el refresh es inválido, no es `type=refresh` o la cuenta está
 *     desactivada (UME #221).
 *
 * Reglas:
 * - Un solo refresh en vuelo (single-flight): N llamadas que fallan a la vez
 *   comparten la misma promesa y no disparan N refreshes.
 * - Reintento ÚNICO por request tras un 401: si el reintento vuelve a fallar,
 *   el error sube tal cual y `onAccountError` cierra sesión como hoy. Nunca hay
 *   loop: el refresh se hace con axios crudo, no pasa por Fetch*, y el reintento
 *   no vuelve a entrar aquí.
 * - Si el refresh responde 4xx, el refresh token está muerto: lo borramos para
 *   no intentarlo en cada request. Un error de red NO lo borra.
 * - Refresco proactivo (`ensureFreshSession`): si al access token le quedan
 *   menos de REFRESH_THRESHOLD_MS, se renueva antes de que expire. Si falla no
 *   pasa nada visible: el 401 posterior hará el intento reactivo y, si tampoco,
 *   el logout de siempre.
 *
 * Nada de esto cambia el backend ni `ACCESS_TOKEN_DURATION`; es el
 * prerrequisito para poder acortar el TTL sin obligar a re-login diario.
 */

/** Renovar cuando quedan menos de 6 h de vida. Con TTL de 24 h, quien abre la
 *  app una vez al día nunca ve un 401; con el TTL actual (7 d) casi nunca actúa. */
export const REFRESH_THRESHOLD_MS = 6 * 60 * 60 * 1000;

// Misma resolución de host que FetchV2.getURL para `userManagementEngine`
// (se duplica a propósito para no crear un import circular Fetch ↔ refresh).
const UME_PATH = '/api/v1/heru-user-management-engine';
const UME_LOCAL_PORT = 3022;

const refreshUrl = (): string => {
	const host = import.meta.env.VITE_NEW_GATEWAY_HOST || 'https://apis.heru.app';
	const env = import.meta.env.VITE_ENV_HOST || 'prod';
	const localServices = String(import.meta.env.VITE_LOCAL_SERVICES || '')
		.split(',')
		.map((s: string) => s.trim());
	if (env === 'dev' || localServices.includes('userManagementEngine')) {
		return `http://localhost:${UME_LOCAL_PORT}${UME_PATH}/auth/refresh`;
	}
	return `${host}${UME_PATH}/auth/refresh`;
};

/** `exp` del JWT en ms, o null si no se puede leer. Acepta exp en segundos
 *  (Google) o en ms (firmador OTP), igual que hace el servidor. */
export const decodeJwtPayload = (token: string): Record<string, unknown> | null => {
	try {
		const part = token.split('.')[1];
		if (!part) return null;
		const normalized = part.replace(/-/g, '+').replace(/_/g, '/');
		const padded = normalized.padEnd(
			normalized.length + ((4 - (normalized.length % 4)) % 4),
			'=',
		);
		const json = decodeURIComponent(
			Array.prototype.map
				.call(atob(padded), (c: string) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
				.join(''),
		);
		return JSON.parse(json);
	} catch {
		return null;
	}
};

export const decodeExpMs = (token: string): number | null => {
	const exp = decodeJwtPayload(token)?.exp;
	if (typeof exp !== 'number') return null;
	return exp < 1e12 ? exp * 1000 : exp;
};

let inFlightRefresh: Promise<boolean> | null = null;

const doRefresh = async (): Promise<boolean> => {
	const storage = getStorage();
	const refreshToken = storage.getItem(LOCAL_STORAGE_KEY.REFRESH);
	if (!refreshToken) return false;

	try {
		const { data } = await axios.post(
			refreshUrl(),
			{ refresh_token: refreshToken },
			{ headers: { 'Content-Type': 'application/json' } },
		);
		const body = data?.resource ?? data;
		const newAccess: string | undefined = body?.access_token?.access_token;
		const newRefresh: string | undefined = body?.refresh_token?.refresh_token;
		if (!newAccess) return false;

		storage.setItem(LOCAL_STORAGE_KEY.TOKEN, newAccess);
		if (newRefresh) storage.setItem(LOCAL_STORAGE_KEY.REFRESH, newRefresh);
		storage.removeItem(LOCAL_STORAGE_KEY.SESSION_EXPIRED);
		return true;
	} catch (error: any) {
		const status: number | undefined = error?.response?.status;
		// 4xx = el refresh ya no sirve (vencido, rotado, cuenta desactivada):
		// lo tiramos para no reintentar un token muerto en cada request.
		if (typeof status === 'number' && status >= 400 && status < 500) {
			storage.removeItem(LOCAL_STORAGE_KEY.REFRESH);
		}
		return false;
	}
};

/** Renueva el par de tokens. Single-flight: llamadas concurrentes comparten
 *  la misma promesa. Resuelve `true` si hay access token nuevo en storage. */
export const refreshSession = (): Promise<boolean> => {
	if (inFlightRefresh) return inFlightRefresh;
	inFlightRefresh = doRefresh().finally(() => {
		inFlightRefresh = null;
	});
	return inFlightRefresh;
};

/** Refresco proactivo: renueva si el access token expira en menos de
 *  REFRESH_THRESHOLD_MS (o ya expiró). No cierra sesión si falla. */
export const ensureFreshSession = async (): Promise<boolean> => {
	const storage = getStorage();
	const token = storage.getItem(LOCAL_STORAGE_KEY.TOKEN);
	if (!token) return false;
	if (!storage.getItem(LOCAL_STORAGE_KEY.REFRESH)) return true; // p.ej. login por ?token= (impersonate): no hay con qué refrescar

	const expMs = decodeExpMs(token);
	if (expMs === null) return true; // token sin exp legible: no tocamos nada
	if (expMs - Date.now() > REFRESH_THRESHOLD_MS) return true;

	return refreshSession();
};

const is401 = (error: any): boolean => error?.response?.status === 401;

/**
 * Ejecuta `perform`; si responde 401 y la request iba con Bearer, refresca UNA
 * vez y reintenta UNA vez. `perform` debe leer el token de storage en cada
 * invocación (no capturarlo fuera) para que el reintento use el nuevo.
 * Si el refresh falla o el reintento vuelve a fallar, relanza el error original
 * del reintento para que el caller haga lo de siempre (`onAccountError`).
 */
export const executeWithRefresh = async <T>(
	perform: () => Promise<T>,
	{ retryOn401 = true }: { retryOn401?: boolean } = {},
): Promise<T> => {
	try {
		return await perform();
	} catch (error) {
		if (!retryOn401 || !is401(error)) throw error;
		const refreshed = await refreshSession();
		if (!refreshed) throw error;
		return await perform();
	}
};
